import { Container, Group, Grid, Button, GridCol, Title, Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import { ICurrency } from '../../../../libraries/currencies';
import { Transaction } from '../../../../libraries/transactions';
import { AppState } from '../../../client-redux/reducers';
import ModalComponent from '../../../components/modal';
import DisplayField from '../../../components/display-field';
import { Language } from '../../../languages/languageHandler';
import { formatDateTimeLabel } from '../../../utils/date';
import { formatCurrAmount, getLocationName, formatTransactionType } from '../../../utils/script';
import { processTransactionData } from '../transaction-data';
import TransactionTable from '../transaction-table';
import PeriodsTable from '../cash-collection-table';
import { ReactComponent as CheckIcon } from '../../../../assets/img/check.svg';
import { IconFileTypePdf, IconDownload } from '@tabler/icons-react';
import './styles.scss';

interface ShowTransactionProps {
  transaction: Transaction;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
  currency: ICurrency;
  getLog: () => void;
  getReport: () => void;
  isDeposit?: boolean;
}

const ShowTransaction = ({ transaction, openDetails, hideBtn, getLog, getReport }: ShowTransactionProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const matchingCurrency = currencies?.find(c => c.cid === transaction.cid);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const processed = processTransactionData(transaction, currencies);
  const receivedBlocks = processed.filter(p => p.received);
  const dispensedBlocks = processed.filter(p => p.dispensed);

  return (
    <>
      <ModalComponent
        show={openDetails}
        handleClick={hideBtn}
        title={`${formatTransactionType(lang, transaction.type)} ${transaction.eid}-${transaction.period}-${transaction.code}`}
        content={
          <>
            <Container classNames={{ root: 'transaction-details' }}>
              <Group className="details">
                <Grid gap="sm">
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_ID} content={transaction?.eid} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_LOCAL} content={getLocationName(transaction.sid)} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLASS_CODE} content={transaction?.code} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_DATE_TIME}
                      content={formatDateTimeLabel(transaction?.dateTime, region)}
                    />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_FIRST_PERIOD_OPEN_DATE}
                      content={
                        (transaction?.cashCollectionData?.beginDate &&
                          formatDateTimeLabel(transaction.cashCollectionData.beginDate, region)) ||
                        '-'
                      }
                    />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_LAST_PERIOD_CLOSE_DATE}
                      content={
                        (transaction?.cashCollectionData?.endDate &&
                          formatDateTimeLabel(transaction.cashCollectionData.endDate, region)) ||
                        '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_TOTAL_INVOICED}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction?.cashCollectionData?.invoiced ?? 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_TOTAL_COLLECTED}
                      content={matchingCurrency ? formatCurrAmount(transaction?.value || 0, matchingCurrency) : '-'}
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_EXCESS}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction?.cashCollectionData?.excess || 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_CUMULATIVE_EXCESS_PREVIOUS}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(
                              transaction?.cashCollectionData?.prevCumulativeExcess || 0,
                              matchingCurrency
                            )
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_CUMULATIVE_EXCESS}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction?.cashCollectionData?.cumulativeExcess || 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_TOTAL_LOADED}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction?.cashCollectionData?.loaded || 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_WORKING_CAPITAL_EXCESS_REMOVED}
                      content={
                        transaction?.cashCollectionData?.excessWCRemoved ? <CheckIcon className="status-ok" /> : '-'
                      }
                    />
                  </GridCol>
                </Grid>
              </Group>

              {receivedBlocks.length > 0 && (
                <>
                  <Title className="table-titles">{lang.CURRENCY_TABLE_CASH_IN}</Title>
                  {receivedBlocks.map((block, idx) => (
                    <TransactionTable key={`rec-${idx}`} currency={block.currency} cash={block.denominations} />
                  ))}
                </>
              )}

              {transaction?.cashCollectionData?.periods && (
                <>
                  <Title className="table-titles">{lang.CASH_COLLECTION_DETAIL}</Title>
                  <PeriodsTable
                    currency={matchingCurrency}
                    cashCollectionData={transaction?.cashCollectionData}
                    amount={transaction.value || 0}
                    cumulativeExcess={transaction?.cashCollectionData?.cumulativeExcess || 0}
                    prevCumulativeExcess={transaction?.cashCollectionData?.prevCumulativeExcess || 0}
                  />
                </>
              )}

              {dispensedBlocks.length > 0 && (
                <>
                  <Title className="table-titles">{lang.CURRENCY_TABLE_CASH_OUT}</Title>
                  {dispensedBlocks.map((block, idx) => (
                    <TransactionTable key={`dis-${idx}`} currency={block.currency} cash={block.denominations} />
                  ))}
                </>
              )}

              <Group className="buttons">
                <Button radius="md" onClick={() => getLog()}>
                  <IconDownload stroke={2} />
                  Logs
                </Button>
                <Button radius="md" onClick={() => getReport()}>
                  <IconFileTypePdf stroke={2} />
                  Report
                </Button>
              </Group>

              {transaction.softVersion && (
                <Group className="footer-version">
                  <Text>{`${lang.EQUIPMENT_SOFTWARE_VERSION}: ${transaction?.softVersion}`}</Text>
                </Group>
              )}
            </Container>
          </>
        }
      />
    </>
  );
};

export default ShowTransaction;
